import { useCallback } from 'react';
import { Download, FileQuestion } from 'lucide-react';
import { PermissionGate } from '../../../../auth/PermissionGate';
import { Button } from '../../../../components/Button';
import { EmptyState } from '../../../../components/EmptyState';
import { ErrorState } from '../../../../components/ErrorState';
import { Skeleton } from '../../../../components/Skeleton';
import { useBlobPreview } from '../../../../hooks/useBlobPreview';
import type { RoutingFileReadApi } from '../routingApi';
import type { RoutingAttachmentItem } from '../routingTypes';

/**
 * One filed file, shown in place — the Repository document preview's shape, for a routed
 * document's attachments.
 *
 * **It renders only what can be rendered INERTLY** — `useBlobPreview` owns the allow-list, the
 * verified type and the re-typed blob. An image goes in an `<img>`, a PDF to the browser's own
 * viewer; anything else says it cannot be shown and offers the download.
 *
 * **Mounted only while the Preview tab is open** — mounting is what fetches it.
 */
export function AttachmentViewer({
  recordId,
  api,
  attachment,
  downloadPermission,
  onDownload,
}: {
  recordId: string;
  api: RoutingFileReadApi;
  attachment: RoutingAttachmentItem;
  downloadPermission: string;
  onDownload: () => void;
}) {
  const load = useCallback(
    (signal: AbortSignal) => api.download(recordId, attachment.id, signal),
    [api, recordId, attachment.id],
  );

  const { kind, url, loading, error } = useBlobPreview(attachment.id, attachment.contentType, load);

  const download = (
    <PermissionGate permission={downloadPermission}>
      <div className="w-full max-w-sm">
        <Button
          id={`routing-attachment-${attachment.id}-download`}
          variant="secondary"
          fullWidth
          leading={<Download className="h-4 w-4" aria-hidden="true" />}
          onClick={onDownload}
        >
          Download
        </Button>
      </div>
    </PermissionGate>
  );

  if (kind === 'none') {
    return (
      <div className="flex h-full min-h-[420px] flex-col items-center justify-center gap-4">
        <FileQuestion className="h-10 w-10 text-slate-300" aria-hidden="true" />
        <EmptyState
          minHeight="min-h-0"
          message={`This file is ${attachment.contentType}, which cannot be shown here. Download it to open it in the application it belongs to.`}
        />
        {download}
      </div>
    );
  }

  if (loading) {
    return <Skeleton className="h-full min-h-[420px] w-full" />;
  }

  // A failed preview is not a failed page — the download goes through a different path.
  if (error || !url) {
    return (
      <div className="flex h-full min-h-[420px] flex-col items-center justify-center gap-4">
        <ErrorState message={error?.message ?? 'Could not load the preview.'} />
        {download}
      </div>
    );
  }

  if (kind === 'image') {
    return (
      <div className="modern-scrollbar flex h-full min-h-[420px] items-start justify-center overflow-auto bg-slate-50 p-4">
        {/* The uploaded name is what this image IS as far as the system knows — so it is the alt. */}
        <img src={url} alt={attachment.fileName} className="max-w-full rounded-lg border border-slate-200 bg-white shadow-sm" />
      </div>
    );
  }

  return (
    <iframe
      src={url}
      title={`Preview of ${attachment.fileName}`}
      className="h-full min-h-[420px] w-full border-0 bg-slate-50"
    />
  );
}
